import { useRef, useEffect } from "react";
import {
  getSlotControllers,
  hasSlotController,
  markSlotMounted,
  trackWrapMount,
  PluginRuntime,
  pluginLog,
} from "@markedesk/plugin-sdk/ui";
import { useChannelPlugins } from "../ChannelIcon";
import { mountPluginJsx } from "./pluginJsx";
import api from "../../services/api";

/**
 * Motor dos slots de WRAP — o lado imperativo da integração de plugins.
 *
 * Era o corpo do `PluginWrap`; virou hook para que o `PluginBridge` possa usá-lo
 * em TODOS os pontos de extensão, e não só nos quatro que montavam o componente
 * à mão. A divisão anterior (declarativo em 20 pontos, imperativo em 4) não
 * estava escrita em lugar nenhum e produzia falha muda: `markedesk.registerSlot`
 * aceitava qualquer nome, registrava, e nunca era chamado se aquele ponto usasse
 * o outro mecanismo. Dois casos reais viveram assim — o botão do plugin-tour na
 * navbar e o "Enviar template" do notificamehub no modal de Novo Ticket, este
 * último desde que nasceu.
 *
 * Entrega ao controlador do plugin a SlotApi (hide/show/add/clear/mount/
 * mountJsx/openModal/onTicketMessage) e o ciclo de vida onMount /
 * onContextChange / onUnmount. Tudo sobre DOM real — funciona no bundle de
 * produção, sem JSX nem import do lado do plugin.
 *
 * @param {string} slot     nome do slot (ex: "message-input")
 * @param {object} context  dados entregues ao controlador
 * @param {object} [options]
 * @param {string} [options.layout]   como o wrapper participa do layout — ver PluginSlotWrap
 * @param {boolean} [options.enabled] false = este ponto NÃO executa controladores
 *   (só desenha as ações declaradas). O slot continua sendo anunciado como
 *   renderizado, e é essa distinção que permite ao SDK acusar o caso em que
 *   TODOS os pontos de um slot estão desligados — o controlador registrado
 *   nunca rodaria e ninguém saberia.
 * @returns {{hasControllers: boolean, originalRef: object, layerRef: object}}
 */
export default function usePluginSlot(slot, context = {}, options = {}) {
  const { layout = "contents", enabled = true, onInsertText } = options;
  const plugins = useChannelPlugins();
  const originalRef = useRef(null);   // wrapper do elemento original
  const layerRef = useRef(null);      // camada onde o plugin injeta elementos
  const slotApisRef = useRef([]);     // SlotApi por controlador (para lifecycle)
  const hasControllers = enabled && hasSlotController(slot);

  // Anuncia que ESTE slot existe de fato na tela, e se aqui ele executa
  // controladores. É o que permite avisar, em desenvolvimento, quando um plugin
  // registra um controlador que nada executa — o modo de falha que motivou esta
  // refatoração.
  useEffect(() => {
    if (!slot) return; // ponto sem slot: não renderizou slot nenhum
    markSlotMounted(slot, { wrap: enabled });
  }, [slot, enabled]);

  /**
   * Chave do contexto — serve de dependência do efeito de mudança de contexto e
   * de identidade da montagem (para detectar dois pontos servindo o mesmo slot).
   *
   * Só é calculada quando há controlador: sem plugin nenhum não há a quem
   * notificar, e serializar o contexto a cada render sairia caro justamente nos
   * pontos que mais renderizam — três slots por mensagem na lista, dois por item
   * na lista de atendimentos, a conexão inteira (com channelData) por linha em
   * Conexões. Ordena as chaves porque dois pontos do mesmo slot montam o mesmo
   * contexto escrito em ordens diferentes.
   */
  const contextKey = hasControllers ? stableKey(context) : "";
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;

  /**
   * O nó onde o plugin monta React precisa combinar com o wrapper escolhido:
   * com `contents`, um `flex:1` aqui reintroduziria exatamente o esticamento que
   * o `contents` evita. Antes esse estilo estava duplicado em `mount` e
   * `mountJsx`, cada um com sua cópia da string.
   */
  const estiloDoNoReact = layout === "contents"
    ? "display:contents"
    : "display:flex;flex:1;width:100%";

  /**
   * O que `show()` precisa devolver ao nó nativo.
   *
   * `style.display = ""` apenas APAGA a regra inline — e como o wrapper do
   * conteúdo nativo nasce com `display: contents` (ou `flex`), limpar a
   * propriedade o rebaixaria a `block` na volta do `hide()`, quebrando o layout
   * justamente na tela que o plugin acabou de liberar.
   */
  const displayNativo = layout === "flex" ? "flex" : layout === "block" ? "" : "contents";

  // Constrói a SlotApi para um controlador de um plugin específico.
  const makeSlotApi = (pluginId) => {
    const plugin = plugins.find((p) => p.id === pluginId || p.channel === pluginId);
    const added = [];
    const apiObj = {
      context,
      routePath: plugin?.routePath || null,
      api,
      /**
       * Escreve na caixa de digitação, quando o ponto oferece isso (hoje o
       * message-input). Existia só no motor declarativo (resultType
       * "insert-text"): quem usava registerSlot conseguia desenhar um seletor,
       * mas não tinha como entregar o que a pessoa escolheu.
       */
      insertText(texto) {
        if (typeof onInsertText === "function" && texto) onInsertText(String(texto));
      },
      hide() { if (originalRef.current) originalRef.current.style.display = "none"; },
      show() { if (originalRef.current) originalRef.current.style.display = displayNativo; },
      remove() { if (originalRef.current) originalRef.current.innerHTML = ""; },
      add(el) {
        const node = document.createElement("div");
        if (el.html != null) node.innerHTML = el.html;
        if (typeof el.onClick === "function") {
          node.style.cursor = "pointer";
          node.addEventListener("click", () => el.onClick(apiObj));
        }
        layerRef.current && layerRef.current.appendChild(node);
        if (typeof el.onMount === "function") {
          try { el.onMount(node, apiObj); } catch (e) { /* eslint-disable-line */ }
        }
        const handle = { remove() { node.remove(); } };
        added.push(handle);
        return handle;
      },
      clear() {
        added.splice(0).forEach((h) => h.remove());
        // Desmonta qualquer árvore React montada pelo plugin antes de limpar.
        if (apiObj.__reactNode && PluginRuntime.reactDOM) {
          try { PluginRuntime.reactDOM.unmountComponentAtNode(apiObj.__reactNode); } catch (e) { /* noop */ }
          try { apiObj.__reactNode.remove(); } catch (e) { /* noop */ }
          apiObj.__reactNode = null;
        }
        if (layerRef.current) layerRef.current.innerHTML = "";
        apiObj.show();
      },
      /**
       * Monta um componente REACT de verdade no slot, usando o React DO APP
       * (PluginRuntime.react) — mesma versão, sem bundle do plugin. `factory`
       * é `(React, ctx) => ReactElement`. Esconde o elemento original e renderiza
       * a árvore React num container próprio. Reidempotente (re-render no mesmo nó).
       */
      mount(factory) {
        const React = PluginRuntime.react;
        const ReactDOM = PluginRuntime.reactDOM;
        if (!React || !ReactDOM || typeof factory !== "function") return;
        // Remove HTML solto (slot.add) antes de montar React no mesmo slot.
        added.splice(0).forEach((h) => h.remove());
        garantirNoReact(apiObj);
        apiObj.hide(); // esconde o indicador nativo enquanto o plugin renderiza
        try {
          ReactDOM.render(factory(React, apiObj.context), apiObj.__reactNode);
        } catch (e) { /* eslint-disable-line no-console */ console.error("[PluginSlot] mount erro", e); }
      },
      /**
       * Monta um componente escrito em JSX (string). O Babel (CDN, sob demanda)
       * transpila o JSX → createElement e renderiza com o React DO APP. Assim o
       * plugin escreve React "igual TS" (JSX), sem build próprio.
       *
       * `jsx` é o corpo de uma função `(React, props) => <JSX/>`. Ex.:
       *   slot.mountJsx("return <div>{props.message}</div>", props)
       */
      async mountJsx(jsx, props) {
        try {
          // Checa tudo que o motor precisa ANTES de esconder o nativo: se faltar
          // Babel ou o React do app, a tela fica como estava em vez de sumir.
          const Babel = await loadBabel();
          if (!Babel || !PluginRuntime.react || !PluginRuntime.reactDOM) return;

          // Remove HTML solto (slot.add) antes de montar React no mesmo slot.
          added.splice(0).forEach((h) => h.remove());
          garantirNoReact(apiObj);
          apiObj.hide();

          // Mesmo motor do modal, do resultado de action e do campo de settings.
          // É ele que entrega React, mui, icons, idioma e runtime ao JSX. Este
          // host tinha uma cópia própria da renderização e ficou de fora quando
          // a injeção foi para o motor: o card do Echo no dashboard quebrava com
          // `props.mui is undefined` (14-09-2026, HML).
          //
          // O cache é por slot e sobrevive entre chamadas: o motor compila a
          // string uma vez e reaproveita o mesmo tipo de componente, então o
          // React re-renderiza sem remontar e o useState do plugin não zera.
          if (!apiObj.__jsxCache) apiObj.__jsxCache = {};
          await mountPluginJsx(
            apiObj.__reactNode,
            jsx,
            props || apiObj.context,
            apiObj.__jsxCache
          );
        } catch (e) { /* eslint-disable-line no-console */ console.error("[PluginSlot] mountJsx erro", e); }
      },
      openModal(opts) { return openDomModal(opts); },
      onTicketMessage(ticketId, cb) { return PluginRuntime.onTicketMessage(ticketId, cb); },
    };

    // Cria (uma vez) o container da árvore React do plugin. Ficava repetido nos
    // dois métodos de montagem, cada um com sua cópia do estilo.
    function garantirNoReact(alvo) {
      if (alvo.__reactNode) return;
      alvo.__reactNode = document.createElement("div");
      alvo.__reactNode.style.cssText = estiloDoNoReact;
      // Marca o nó do plugin: quem hospeda o slot (ex.: o painel lateral, que
      // fica oculto enquanto vazio) lê daqui se há conteúdo desenhado.
      alvo.__reactNode.setAttribute("data-plugin-react-node", "");
      layerRef.current && layerRef.current.appendChild(alvo.__reactNode);
    }

    return apiObj;
  };

  // Mount / unmount dos controladores.
  useEffect(() => {
    pluginLog("PluginSlot mount effect", { slot, hasControllers, ctx: context });
    if (!hasControllers) return undefined;
    let controllers = getSlotControllers(slot);
    // GUARD genérico: no slot de status de conexão, só monta o controller do
    // plugin DAQUELE canal — senão o slot de um plugin (ex: botão "Conectar" do
    // wwebjs) vaza para conexões de OUTROS canais (whatsapp nativo, baileys). O
    // pluginId do controller deve casar com o channel/id da conexão. #4137
    if ((slot === "connection-status" || slot === "connection-actions") && context.channel) {
      // O pluginId do controller de um canal é o próprio channel (plugin.id ===
      // channel, ex: "whatsapp_wwebjs"). Só monta quem provê o canal da linha.
      controllers = controllers.filter(({ pluginId }) => pluginId === context.channel);
    }
    pluginLog("PluginSlot onMount controllers", { slot, count: controllers.length });
    // Identidade desta montagem. Duas montagens vivas do mesmo slot com o mesmo
    // contexto significam dois pontos da tela servindo o mesmo lugar — o SDK
    // avisa em desenvolvimento (montagem por linha/item tem contexto distinto e
    // não dispara nada).
    const releaseWrap = trackWrapMount(slot, contextKeyRef.current);
    const apis = controllers.map(({ pluginId, controller }) => {
      const slotApi = makeSlotApi(pluginId);
      pluginLog("PluginSlot chamando onMount", { pluginId, routePath: slotApi.routePath });
      try { controller.onMount && controller.onMount(slotApi); } catch (e) { /* eslint-disable-line no-console */ console.error("[PluginSlot] erro onMount", e); }
      return { slotApi, controller };
    });
    slotApisRef.current = apis;
    return () => {
      apis.forEach(({ slotApi, controller }) => {
        try { controller.onUnmount && controller.onUnmount(slotApi); } catch (e) { /* eslint-disable-line */ }
        // Desmonta a árvore React do plugin e solta os nós injetados por add().
        // O onUnmount do plugin cuida do que é DELE (listeners de socket, por
        // exemplo); o DOM que ESTE hook criou é responsabilidade daqui — sem
        // isto, remontagem (StrictMode, troca de ticket, re-render de lista)
        // deixaria árvore React órfã presa ao nó antigo.
        try { slotApi.clear(); } catch (e) { /* noop */ }
      });
      slotApisRef.current = [];
      releaseWrap();
    };
    // plugins.length: quando o /plugins resolve DEPOIS do mount (1ª carga),
    // os controladores são registrados tardiamente; reagir à lista garante que
    // o efeito re-rode e o controlador seja montado (senão o wrap só apareceria
    // após reload).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot, hasControllers, plugins.length]);

  // Mudança de contexto → re-decide (atualiza a referência e notifica).
  useEffect(() => {
    pluginLog("PluginSlot contextChange effect", { slot, apis: slotApisRef.current.length, ticketId: context.ticketId });
    slotApisRef.current.forEach(({ slotApi, controller }) => {
      slotApi.context = context;
      try { controller.onContextChange && controller.onContextChange(slotApi); } catch (e) { /* eslint-disable-line no-console */ console.error("[PluginSlot] erro onContextChange", e); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  return { hasControllers, originalRef, layerRef };
}

// ── Infraestrutura de módulo (Babel sob demanda e modal via DOM) ────────────

/**
 * Serializa o contexto com as chaves em ordem alfabética, em qualquer nível.
 *
 * `JSON.stringify` puro depende da ordem de inserção: os dois pontos que servem
 * `message-input` montam o MESMO contexto (mesmo ticket, mesma conexão) escrito
 * em ordens diferentes, e sem ordenar a detecção de wrap duplicado não veria a
 * duplicidade. Contexto impossível de serializar (ciclo) devolve "" em vez de
 * derrubar o render.
 */
function stableKey(obj) {
  try {
    return JSON.stringify(obj, (key, value) => {
      if (!value || typeof value !== "object" || Array.isArray(value)) return value;
      return Object.keys(value).sort().reduce((acc, k) => { acc[k] = value[k]; return acc; }, {});
    });
  } catch (e) {
    return "";
  }
}

// Carregamento sob demanda do @babel/standalone — só baixa quando um plugin usa
// JSX (slot.mountJsx). Não incha o bundle do app. Servido LOCALMENTE pelo app
// (public/vendor/babel.min.js); o browser cacheia o arquivo. Fallback ao CDN se
// o arquivo local não existir. Cacheia a Promise (carrega 1x por sessão).
const BABEL_LOCAL = "/vendor/babel.min.js";
const BABEL_CDN = "https://unpkg.com/@babel/standalone@7/babel.min.js";
let babelPromise = null;

function injectScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(window.Babel);
    s.onerror = () => reject(new Error("Falha ao carregar " + src));
    document.head.appendChild(s);
  });
}

function loadBabel() {
  if (window.Babel) return Promise.resolve(window.Babel);
  if (babelPromise) return babelPromise;
  // Tenta o local (cacheado pelo browser); se falhar, cai pro CDN.
  babelPromise = injectScript(BABEL_LOCAL)
    .catch(() => injectScript(BABEL_CDN))
    .catch((e) => { babelPromise = null; throw e; });
  return babelPromise;
}

/**
 * Abre um modal via DOM direto (sem depender do PluginHtmlModalHost nem de
 * instância de módulo). Cria um overlay fixo + caixa branca, injeta o html e
 * chama onMount(content, resolve, close). Retorna Promise que resolve com o
 * valor passado a resolve() (ou undefined ao fechar).
 */
function openDomModal(opts) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v) => { if (done) return; done = true; overlay.remove(); resolve(v); };

    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:13000;" +
      "display:flex;align-items:center;justify-content:center;padding:16px";
    overlay.addEventListener("click", (e) => { if (e.target === overlay) finish(undefined); });

    // opts.width segue o mesmo contrato tipado do SDK (OpenHtmlModalOptions,
    // ver packages/plugin-sdk/src/ui/hooks.ts) que openHtmlModal/
    // PluginHtmlModalHost já respeitam via MUI Dialog maxWidth — replica os
    // mesmos breakpoints aqui pro slot.openModal NÃO ficar desalinhado da outra
    // implementação. Sem width → default "sm" (520px, retrocompat total).
    const WIDTH_PX = { xs: 444, sm: 600, md: 900, lg: 1200, xl: 1536 };
    const maxWidth = (WIDTH_PX[opts.width] || 520) + "px";
    const box = document.createElement("div");
    box.style.cssText =
      "background:#fff;border-radius:12px;max-width:" + maxWidth + ";width:100%;" +
      "max-height:85vh;overflow:auto;box-shadow:0 12px 40px rgba(0,0,0,.25)";

    if (opts.title) {
      const h = document.createElement("div");
      h.style.cssText = "padding:14px 18px;border-bottom:1px solid #eee;font-weight:600;font-size:16px";
      h.textContent = opts.title;
      box.appendChild(h);
    }
    const content = document.createElement("div");
    content.style.cssText = "padding:18px";
    box.appendChild(content);
    overlay.appendChild(box);
    document.body.appendChild(overlay);

    // Modo REACT/JSX: transpila (Babel) e monta o React do app (hooks funcionam).
    // O componente recebe props + { resolve, close } para fechar o modal.
    if (opts.jsx) {
      const cache = {};
      mountPluginJsx(
        content,
        opts.jsx,
        { ...(opts.props || {}), resolve: (v) => finish(v), close: () => finish(undefined) },
        cache
      ).catch((e) => { /* eslint-disable-line no-console */ console.error("[PluginSlot modal] jsx erro:", e); });
      return;
    }

    // Modo HTML (legado): innerHTML + onMount.
    content.innerHTML = opts.html || "";
    if (typeof opts.onMount === "function") {
      try { opts.onMount(content, (v) => finish(v), () => finish(undefined)); }
      catch (e) { /* eslint-disable-line no-console */ console.error("[PluginSlot modal] onMount erro:", e); }
    }
  });
}
